import { isValidEmail } from "@redotech/email/address";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import type { EmailBlock as Section } from "@redotech/redo-marketing-templates-common/email-template";

import { sentenceCase } from "@redotech/string/transform";
import {
  LeafFieldPath,
  LeafFieldPathValue,
  Primitive,
} from "@redotech/util/type";
import { urlRegex } from "@redotech/util/url";
import {
  JSXElementConstructor,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import isMobilePhone from "validator/lib/isMobilePhone";
import { Accordion } from "./accordion";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "./arbiter-components/checkbox/redo-button-tab-group";
import { RedoCollapseSubsection } from "./arbiter-components/collapse/redo-collapse-section";
import { RedoColorPickerBadge } from "./arbiter-components/color-picker/redo-color-picker-badge";
import { Divider } from "./arbiter-components/divider/divider";
import { RedoIncrementDecrement } from "./arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoPhoneNumberInput } from "./arbiter-components/input/redo-phone-number-input";
import {
  formatDelimitersMessage,
  RedoTagDelimiter,
  RedoTagInputField,
} from "./arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "./arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "./arbiter-components/list/redo-list";
import { LeadingItem } from "./arbiter-components/list/redo-list-item-lead";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "./arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "./arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "./arbiter-components/toggle/redo-toggle";
import { ImageEditorModal } from "./email-builder-shared/builder/inspectors/image-editor/image-editor-modal";
import { FileUpload } from "./file-upload";
import { Flex } from "./flex";
import { ImageUpload, VideoUpload } from "./image-upload";
import { InfoTooltip } from "./info-tooltip";
import { updatedObject } from "./inspector-utils";
import * as inspectorStyles from "./inspector.module.css";
import {
  LabeledInput,
  LabelOrientation,
  LabelPosition,
  LabelSize,
} from "./labeled-input";
import { Slider } from "./slider";
import { Tabs, TabSize } from "./tab";
import { Text } from "./text";

// can only be used on fields, not sections,
// since sections need to change the form's state,
// and the predicate function doesn't have a way to do that.
type Predicate<T> = Omit<PathPredicate<T>, "label"> | ((object: T) => boolean);

interface PathPredicate<T> {
  path: LeafFieldPath<T>;
  invert?: boolean;
  label?: string;
}

// This will infer the actual allowed types for the path, e.g, if T is { a: 1 | 2 | 3 },
// Then the allowed values in a "select" widget for P = "a" will be 1 | 2 | 3
// A sad limitation is that Typescript can't infer this for generic types, so a few functions around the codebase
// have to cast in order to use this. But in 90% of use cases, this provides perfect type safety.
// (before this, "select" and "button-tab-group" widgets were not type-safe at all)
type SingleFieldConfig<T> =
  LeafFieldPath<T> extends infer P
    ? P extends LeafFieldPath<T>
      ? PrimitiveFieldConfig<T, P> | NestedInspectorConfig<T>
      : never
    : never;

interface HorizontalLayoutGroupConfig<T> {
  widget: "horizontal-layout-group";
  align?: "flex-start" | "center" | "flex-end";
  fields: SingleFieldConfig<T>[];
}
interface TabbedInspectorsConfig<T> {
  widget: "tabs";
  tabs: { label: string; inspector: InspectorConfig<T> }[];
}

export type FieldConfig<T> =
  | SingleFieldConfig<T>
  | HorizontalLayoutGroupConfig<T>
  | TabbedInspectorsConfig<T>;

export interface SectionConfig<T> {
  fields: FieldConfig<T>[];
  visibleIf?: PathPredicate<T>; // TODO: support Predicate<T> (non-trivial)
  disabledIf?: PathPredicate<T>; // TODO: support Predicate<T> (non-trivial)
  disabled?: {
    predicate: LeafFieldPath<T> | ((object: T) => boolean);
    label?: string;
  };
  label?: string;
  description?: string;
  order?: number;
  hideDividerBefore?: boolean;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

interface GenericField<T> {
  visibleIf?: Predicate<T>;
  disabledIf?: Predicate<T>;
  label?: string | ((object: T) => ReactNode) | null; // undefined defaults to the last part of the path, null means no label
  description?: string;
  /** Renders a help icon with this tooltip next to the label. */
  tooltip?: { title: ReactNode; subtitle?: ReactNode };
}

type NestedInspectorConfig<T> = GenericField<T> &
  (
    | {
        widget: "inspector";
        inspector: InspectorConfig<NonNullable<T>>;
        label: string | ((object: T) => ReactNode) | null;
        Preview?: (object: T) => ReactNode;
        inline?: boolean;
      }
    | {
        widget: "inspector";
        inspector: InspectorConfig<any>;
        nestedPath: keyof T; // should this support nested paths?
        label: string | ((object: T) => ReactNode) | null;
        Preview?: (object: T) => ReactNode;
        inline?: boolean;
      }
  );

/**
 * The base configuration for a primitive (non-object) field.
 *
 * @remarks
 * If more powerful error handling is ever needed, it should be added here.
 * This hasn't been necessary yet, so we're keeping it simple for now.
 *
 * Current assumptions about errors:
 * - Not every widget needs error handling because most of them already make invalid states unrepresentable.
 * - A widget only needs to know its own state, not the state of other widgets, to determine if it's in an error state.
 *
 * Due to these assumptions, error handling is currently done on a per-widget, as-needed basis.
 * If the assumptions above ever change, we can add a generic error handling mechanism instead.
 */
interface BasePrimitiveFieldConfig<T> extends GenericField<T> {
  path: LeafFieldPath<T>;
}

export interface TextFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "text";
  maxLength?: number;
  error?: (value: string) => string | undefined;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  Toolbar?: (props: {
    object: T;
    setObject: (s: T) => void;
    inputRef: RefObject<HTMLInputElement | null>;
  }) => ReactNode;
}

export interface EmailFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "email";
  maxLength?: number;
  error?: (value: string) => string | undefined;
  placeholder?: string;
}

export interface PhoneFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "phone";
  maxLength?: number;
  error?: (value: string) => string | undefined;
  placeholder?: string;
}

export interface UrlFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "url";
  maxLength?: number;
  error?: (value: string) => string | undefined;
  placeholder?: string;
}

export interface TextAreaFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "textarea";
  maxLength?: number;
  placeholder?: string;
  autoExpand?: boolean;
  Toolbar?: (props: {
    object: T;
    setObject: (s: T) => void;
    inputRef: RefObject<HTMLTextAreaElement | null>;
  }) => ReactNode;
  error?: (value: string) => string | undefined;
}

export interface NumberFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "number";
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  /** Shown when the stored value is absent. Defaults to 0. */
  defaultValue?: number;
}

export interface ColorFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "color";
  showAlpha?: boolean;
}

export interface ToggleFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "toggle";
  /** Shown when the stored value is absent. Defaults to off. */
  defaultValue?: boolean;
  conditionalFields?: FieldConfig<T>[];
  alsoToggles?:
    | {
        path: LeafFieldPath<T>;
        whenTurnedOn?: boolean;
        whenTurnedOff?: boolean;
      }
    | {
        path: LeafFieldPath<T>;
        whenTurnedOn?: boolean;
        whenTurnedOff?: boolean;
      }[];
}

export type SelectFieldConfig<
  T,
  P extends LeafFieldPath<T>,
> = BasePrimitiveFieldConfig<T> & {
  widget: "select";
  path: P;
  options: RedoListItem<{
    name: LeafFieldPathValue<T, P>; // TODO: rename to "value"
    displayName?: ReactNode; // TODO: rename to "name"
    DisplayIcon?: JSXElementConstructor<any>;
  }>["value"][];
  defaultValue?: LeafFieldPathValue<T, P>;
};

export interface RadioFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "radio";
  options: RadioGroupOption<Primitive>[];
}

export interface SliderFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "slider";
  min: number;
  max: number;
  step?: number;
}

export type ImageUploadFieldConfig<T> = BasePrimitiveFieldConfig<T> & {
  widget: "image-upload";
  className?: string;
  caption?: string;
  maxFileSizeKb?: number;
  maxImageWidthPx?: number;
  recommendedLongestSidePx?: number;
  autoOptimizeImage?: boolean;
  accept?: string[];
  required?: boolean;
  isCroppingEnabled?: boolean;
  preserveGifs?: boolean; // If true, GIFs won't be compressed
  onImageUrlChanged?: (newImageUrl: string) => void;
};

export interface VideoUploadFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "video-upload";
  className?: string;
  caption?: string;
}

export interface FileUploadFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "file-upload";
  className?: string;
  caption?: string;
  maxFileSizeKb?: number;
  accept?: string[];
  required?: boolean;
}

export interface TagFieldConfig<T> extends BasePrimitiveFieldConfig<T> {
  widget: "tag";
  placeholderText?: string;
  error?: (value: string[]) => string | undefined;
  delimiters?: RedoTagDelimiter[];
}

export type RedoButtonTabGroupConfig<
  T,
  P extends LeafFieldPath<T>,
> = BasePrimitiveFieldConfig<T> & {
  widget: "button-tab-group";
  path: P;
  options: { value: LeafFieldPathValue<T, P>; element: ReactNode }[];
  defaultValue?: LeafFieldPathValue<T, P>;
};

export interface CustomWidgetConfig<T> extends Omit<
  BasePrimitiveFieldConfig<T>,
  "path"
> {
  widget: "custom";
  Element: (props: { value: T; valueChange: (value: T) => void }) => ReactNode;
  error?: (value: T) => string | undefined;
}

export type PrimitiveFieldConfig<T, P extends LeafFieldPath<T>> =
  | TextFieldConfig<T>
  | EmailFieldConfig<T>
  | PhoneFieldConfig<T>
  | UrlFieldConfig<T>
  | NumberFieldConfig<T>
  | ColorFieldConfig<T>
  | ToggleFieldConfig<T>
  | SelectFieldConfig<T, P>
  | SliderFieldConfig<T>
  | ImageUploadFieldConfig<T>
  | VideoUploadFieldConfig<T>
  | FileUploadFieldConfig<T>
  | TagFieldConfig<T>
  | RadioFieldConfig<T>
  | RedoButtonTabGroupConfig<T, P>
  | CustomWidgetConfig<T>
  | TextAreaFieldConfig<T>;

export interface InspectorConfig<T> {
  header?: { title: string; subtitle?: string; icon?: ReactNode };
  sections: SectionConfig<T>[];
  // idea: make it InspectorConfig<T, Context=undefined> to enable fields to access a context object without restoring to "custom" widget
  onClose?: () => void;
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type {
  InspectorComponentProps,
  ValidatableInspector,
} from "./inspector-v2";

interface InspectorProps<T extends object> {
  object: T;
  setObject: (object: T) => void;
  config: InspectorConfig<T>;
  disabled?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  /** Remove internal top padding from first section */
  flush?: boolean;
}

function getNestedFieldValue<T>(object: T, path: LeafFieldPath<T>): any {
  return (
    String(path)
      .split(".")
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      .reduce((o, i) => (o ? o[i] : undefined), object as any)
  );
}

function getPathPredicateValue<T>(
  object: T,
  predicate: Omit<PathPredicate<T>, "label">,
): boolean {
  const value = getNestedFieldValue(object, predicate.path);
  return predicate.invert ? !value : value;
}

export function capitalized(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function allDelimitersToSpaces(str: string): string {
  if (str.includes(" ")) {
    return str.trim(); // Return the string unchanged if it already contains spaces
  }
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to camel Case
    .replace(/[-_]/g, " ")
    .trim();
}

/**
 * @deprecated Use inspector-v2 components (InspectorLayout, InspectorSection, InspectorField) instead
 *
 * For reasons, see https://www.notion.so/redotech/Pros-and-cons-of-the-Inspector-pattern-1e41a6608425808a8325ddc2808a355c?source=copy_link#1e41a6608425809fba10e762a77f7946
 */
export const Inspector = <T extends object>({
  object,
  setObject,
  config,
  disabled = false,
  onValidityChange,
  flush = false,
}: InspectorProps<T>) => {
  const handleFieldChange = (fieldPath: LeafFieldPath<T>, value: any) => {
    const newObject = updatedObject(object, fieldPath, value);
    setObject(newObject);
  };

  const globallyDisabled = disabled;

  const [isValid, setIsValid] = useState(true);
  useEffect(() => {
    const _isValid = config.sections.every((section) => {
      if (
        section.visibleIf &&
        !getPathPredicateValue(object, section.visibleIf)
      ) {
        return true;
      }
      return section.fields.every((field) => {
        if (
          "visibleIf" in field &&
          typeof field.visibleIf === "function" &&
          !field.visibleIf(object)
        ) {
          return true;
        }
        if ("path" in field) {
          const fieldValue = getNestedFieldValue(object, field.path);
          return "error" in field ? !field.error?.(fieldValue) : true;
        } else if (field.widget === "custom" && "error" in field) {
          return !field.error?.(object);
        }
        return true;
      });
    });
    if (isValid !== _isValid) {
      setIsValid(_isValid);
      if (onValidityChange) {
        onValidityChange(_isValid);
      }
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object]);

  return (
    <Flex dir="column" gap="none" w="full">
      {config.header && (
        <Flex dir="column" gap="xs" pb="4xl">
          <Flex dir="row" gap="md" justify="space-between" py="sm" w="full">
            <Flex align="center" dir="row" gap="md" justify="flex-start">
              {config.header.icon}
              <Text fontSize="md" fontWeight="medium">
                {config.header.title}
              </Text>
            </Flex>
            {config.onClose && (
              <RedoButton
                aria-label="Close"
                hierarchy="tertiary"
                IconLeading={XIcon}
                onClick={config.onClose}
                size="sm"
              />
            )}
          </Flex>
          <Text fontSize="xs" textColor="secondary">
            {config.header.subtitle}
          </Text>
        </Flex>
      )}
      {config.sections
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((section, s) => {
          const sectionIsVisible = section.visibleIf
            ? getPathPredicateValue(object, section.visibleIf)
            : true;

          const sectionIsDisabled = section.disabledIf
            ? getPathPredicateValue(object, section.disabledIf)
            : false;

          const sectionHeader = (section.label || section.description) && (
            <Flex dir="column" gap="xs">
              {section.label && (
                <Text fontSize="sm" fontWeight="medium">
                  {section.label}
                </Text>
              )}
              {section.description && (
                <Text fontSize="xs" textColor="secondary">
                  {section.description}
                </Text>
              )}
            </Flex>
          );

          const sectionBody = (
            <Flex dir="column" gap="xl">
              {!!section.visibleIf && (
                <RedoToggle
                  label={section.visibleIf.label ?? section.label}
                  setValue={(e) =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    handleFieldChange(section.visibleIf!.path, e)
                  }
                  size="sm"
                  value={getNestedFieldValue(object, section.visibleIf.path)}
                />
              )}
              {!!section.disabledIf && (
                <RedoToggle
                  label={section.disabledIf.label ?? section.label}
                  setValue={(e) =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    handleFieldChange(section.disabledIf!.path, e)
                  }
                  size="sm"
                  value={getNestedFieldValue(object, section.disabledIf.path)}
                />
              )}
              {sectionIsVisible &&
                section.fields.map((field, i) => (
                  <DynamicDispatchFieldWidget<T>
                    field={field}
                    handleFieldChange={handleFieldChange}
                    implicitlyDisabled={sectionIsDisabled || globallyDisabled}
                    key={"path" in field ? field.path + i : i}
                    object={object}
                    setObject={setObject}
                  />
                ))}
            </Flex>
          );

          if (section.collapsible && section.label) {
            return (
              <div key={section.label + s}>
                {s > 0 && !section.hideDividerBefore && (
                  <Flex mb="2xl" mt="2xl" w="full">
                    <Divider />
                  </Flex>
                )}
                <RedoCollapseSubsection
                  defaultCollapsed={!(section.defaultOpen ?? true)}
                  renderHeader={({ collapsed, leadingIcon, onClick }) => (
                    <Flex
                      align="center"
                      aria-expanded={!collapsed}
                      as="button"
                      bgColor="none"
                      borderStyle="none"
                      borderWidth="none"
                      cursor="pointer"
                      dir="row"
                      gap="xs"
                      justify="space-between"
                      onClick={onClick}
                      p="none"
                      textAlign="left"
                      type="button"
                      w="full"
                    >
                      <Text fontSize="sm" fontWeight="medium">
                        {section.label}
                      </Text>
                      {leadingIcon}
                    </Flex>
                  )}
                  title={section.label}
                >
                  {section.description && (
                    <Flex pb="xl" pt="xs">
                      <Text fontSize="xs" textColor="secondary">
                        {section.description}
                      </Text>
                    </Flex>
                  )}
                  <Flex dir="column" pb="none" pt="md">
                    {sectionBody}
                  </Flex>
                </RedoCollapseSubsection>
              </div>
            );
          }

          return (
            <div data-web="Inspector" key={(section.label ?? "") + s}>
              {s > 0 && !section.hideDividerBefore && (
                <Flex mb="4xl" mt="4xl" w="full">
                  <Divider />
                </Flex>
              )}
              {sectionHeader && (
                <Flex
                  dir="column"
                  mt={s > 0 && section.hideDividerBefore ? "4xl" : undefined}
                  pb="xl"
                >
                  {sectionHeader}
                </Flex>
              )}
              {sectionBody}
            </div>
          );
        })}
    </Flex>
  );
};

function TabbedInspectors<T extends object>({
  object,
  setObject,
  options,
}: {
  object: T;
  setObject: (object: T) => void;
  options: { label: string; inspector: InspectorConfig<T> }[];
}) {
  const firstOption = options[0];
  const [value, valueChange] = useState(firstOption ? firstOption.label : "");
  const selected = options.find((o) => o.label === value);

  if (!selected) {
    return null;
  }

  return (
    <Tabs<string>
      options={options.map((o) => o.label)}
      tab={(label) => label}
      tabSize={TabSize.SMALL}
      value={value}
      valueChange={valueChange}
    >
      <Inspector<T>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        config={selected.inspector as any}
        object={object}
        setObject={setObject}
      />
    </Tabs>
  );
}

function DynamicDispatchFieldWidget<T extends object>({
  object,
  setObject,
  field,
  handleFieldChange,
  implicitlyDisabled,
}: {
  object: T;
  setObject: (object: T) => void;
  field: FieldConfig<T>;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
  implicitlyDisabled: boolean;
}) {
  if (field.widget === "horizontal-layout-group") {
    return (
      <Flex
        align={field.align ?? "flex-start"}
        display="grid"
        style={{
          gridTemplateColumns: Array(field.fields.length).fill("1fr").join(" "),
        }}
      >
        {field.fields.map((f, i) => (
          <PrimitiveFieldWidget<T>
            field={f}
            handleFieldChange={handleFieldChange}
            implicitlyDisabled={implicitlyDisabled}
            key={i}
            object={object}
            setObject={setObject}
          />
        ))}
      </Flex>
    );
  }

  if (field.widget === "tabs") {
    return (
      <TabbedInspectors
        object={object}
        options={field.tabs}
        setObject={setObject}
      />
    );
  }

  return (
    <PrimitiveFieldWidget<T>
      field={field}
      handleFieldChange={handleFieldChange}
      implicitlyDisabled={implicitlyDisabled}
      object={object}
      setObject={setObject}
    />
  );
}

function PrimitiveFieldWidget<T>({
  object,
  setObject,
  field,
  handleFieldChange,
  implicitlyDisabled,
}: {
  object: T;
  setObject: (object: T) => void;
  field: SingleFieldConfig<T>;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
  implicitlyDisabled: boolean;
}) {
  // Check if the field has a showIf condition
  const shouldShow =
    field.visibleIf instanceof Function
      ? field.visibleIf(object)
      : field.visibleIf
        ? getNestedFieldValue(object, field.visibleIf.path)
        : true;

  if (!shouldShow) {
    return null;
  }

  const fieldValue =
    "path" in field ? getNestedFieldValue(object, field.path) : undefined;

  const disabled =
    field.disabledIf instanceof Function
      ? field.disabledIf(object)
      : field.disabledIf
        ? getPathPredicateValue(object, field.disabledIf)
        : false;

  const baseLabel =
    typeof field.label === "function"
      ? field.label(object)
      : (field.label ??
        sentenceCase(
          allDelimitersToSpaces(
            "path" in field ? (String(field.path).split(".").at(-1) ?? "") : "",
          ),
        ));

  const labelComponent = field.tooltip ? (
    <Flex align="center" gap="xs">
      {baseLabel}
      <InfoTooltip boxSize={14} tooltip={field.tooltip} />
    </Flex>
  ) : (
    baseLabel
  );

  const inspectorWidget = (
    <InspectorWidget
      disabled={disabled || implicitlyDisabled}
      field={field}
      fieldValue={fieldValue}
      handleFieldChange={handleFieldChange}
      object={object}
      setObject={setObject}
    />
  );

  const hasConditionalFields =
    field.widget === "toggle" && field.conditionalFields;

  const labeledInput = (
    <LabeledInput
      alignmentOverride={field.widget === "toggle" ? "start" : undefined}
      description={field.description}
      label={labelComponent}
      orientation={LabelOrientation.VERTICAL}
      position={
        field.widget === "toggle" ? LabelPosition.RIGHT : LabelPosition.ABOVE
      }
      size={LabelSize.EXTRA_SMALL}
    >
      {inspectorWidget}
    </LabeledInput>
  );

  if (field.label === null) {
    return inspectorWidget;
  }

  if (hasConditionalFields) {
    const toggledOn =
      fieldValue ??
      (field.widget === "toggle" ? field.defaultValue : undefined);
    return (
      <Flex dir="column" gap="sm">
        {labeledInput}
        {toggledOn && (
          <Flex
            bgColor="tertiary"
            borderRadius="md"
            dir="column"
            gap="lg"
            p="md"
          >
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            {field.conditionalFields!.map((conditionalField, i) => (
              <DynamicDispatchFieldWidget
                field={conditionalField}
                handleFieldChange={handleFieldChange}
                implicitlyDisabled={disabled || implicitlyDisabled}
                key={i}
                object={object}
                setObject={setObject}
              />
            ))}
          </Flex>
        )}
      </Flex>
    );
  }

  return labeledInput;
}

export function InspectorWidget<T, P extends LeafFieldPath<T>>({
  field,
  fieldValue,
  handleFieldChange,
  object,
  setObject,
  disabled = false,
}: {
  field: PrimitiveFieldConfig<T, P> | NestedInspectorConfig<T>;
  fieldValue: any;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
  object: T;
  setObject: (object: T) => void;
  disabled?: boolean;
}) {
  // Necessary because RedoTagInputField has a useEffect that depends on setValue.
  const [tagInputValue, setTagInputValue] = useState("");
  // Necessary because RedoTextInput has a useEffect that depends on setValue.
  const textChangeHandler = useHandler(
    (t: string) =>
      (field.widget === "text" ||
        field.widget === "email" ||
        field.widget === "phone" ||
        field.widget === "url" ||
        field.widget === "textarea") &&
      handleFieldChange(field.path, t),
  );
  const textInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <>
      {field.widget === "text" && (
        <RedoTextInput
          description={field.error?.(fieldValue)}
          maxLength={field.maxLength}
          placeholder={field.placeholder}
          ref={textInputRef}
          setValue={textChangeHandler}
          size={field.size}
          state={
            field.error?.(fieldValue)
              ? "error"
              : disabled
                ? "disabled"
                : "default"
          }
          toolbar={
            field.Toolbar && (
              <field.Toolbar
                inputRef={textInputRef}
                object={object}
                setObject={setObject}
              />
            )
          }
          value={fieldValue || ""}
        />
      )}
      {field.widget === "email" && (
        <EmailFieldWidget
          disabled={disabled}
          field={field}
          fieldValue={fieldValue}
          handleFieldChange={handleFieldChange}
        />
      )}
      {field.widget === "phone" && (
        <PhoneFieldWidget
          disabled={disabled}
          field={field}
          fieldValue={fieldValue}
          handleFieldChange={handleFieldChange}
        />
      )}
      {field.widget === "url" && (
        <UrlFieldWidget
          disabled={disabled}
          field={field}
          fieldValue={fieldValue}
          handleFieldChange={handleFieldChange}
        />
      )}
      {field.widget === "textarea" && (
        <RedoTextareaInputField
          autoExpand={field.autoExpand}
          fullWidth
          placeholder={field.placeholder}
          ref={textareaRef}
          setValue={textChangeHandler}
          toolbar={
            field.Toolbar && (
              <field.Toolbar
                inputRef={textareaRef}
                object={object}
                setObject={setObject}
              />
            )
          }
          value={fieldValue || ""}
        />
      )}
      {field.widget === "number" && (
        <RedoIncrementDecrement
          disabled={disabled}
          fullWidth
          max={field.max}
          min={field.min}
          setValue={(n) =>
            handleFieldChange(
              field.path,
              Math.max(
                field.min ?? -Infinity,
                Math.min(field.max ?? Infinity, Number(n)),
              ),
            )
          }
          size="small"
          step={field.step}
          suffix={field.suffix}
          value={fieldValue ?? field.defaultValue ?? 0}
        />
      )}
      {field.widget === "color" && (
        <RedoColorPickerBadge
          onChange={(c) => handleFieldChange(field.path, c)}
          overlayPicker={{ shouldUse: true }}
          showAlpha={field.showAlpha}
          size="lg"
          value={fieldValue || "#c0c0c0"}
        />
      )}
      {field.widget === "toggle" && (
        <RedoToggle
          disabled={disabled}
          setValue={(e) => {
            handleFieldChange(field.path, e);
            if (field.alsoToggles) {
              const affected = Array.isArray(field.alsoToggles)
                ? field.alsoToggles
                : [field.alsoToggles];

              for (const affectedField of affected) {
                if (e && affectedField.whenTurnedOn) {
                  handleFieldChange(affectedField.path, true);
                } else if (!e && affectedField.whenTurnedOff) {
                  handleFieldChange(affectedField.path, false);
                }
              }
            }
          }}
          value={fieldValue ?? field.defaultValue}
        />
      )}
      {field.widget === "select" && (
        <FieldSelectWidget
          disabled={disabled}
          field={field}
          fieldValue={fieldValue ?? field.defaultValue}
          handleFieldChange={handleFieldChange}
        />
      )}
      {field.widget === "slider" && (
        <Slider
          disabled={disabled}
          max={field.max}
          min={field.min}
          onChange={(s) => handleFieldChange(field.path, s)}
          step={field.step ?? 1}
          value={fieldValue}
        />
      )}
      {field.widget === "image-upload" && (
        <ImageUploadWidget
          disabled={disabled}
          field={field}
          fieldValue={fieldValue}
          handleFieldChange={handleFieldChange}
          object={object}
          setObject={setObject}
        />
      )}
      {field.widget === "video-upload" && (
        <VideoUpload
          caption={field.caption}
          className={field.className}
          disabled={disabled}
          onChange={(i) => handleFieldChange(field.path, i?.href ?? "")}
          value={fieldValue}
        />
      )}
      {field.widget === "file-upload" && (
        <FileUpload
          accept={field.accept}
          caption={field.caption}
          className={field.className}
          disabled={disabled}
          maxFileSizeKb={field.maxFileSizeKb}
          onChange={(i) => handleFieldChange(field.path, i?.href ?? "")}
          showErrorMessages
          value={fieldValue}
        />
      )}
      {field.widget === "tag" && (
        <TagFieldWidget
          disabled={disabled}
          field={field}
          fieldValue={fieldValue}
          handleFieldChange={handleFieldChange}
          setTagInputValue={setTagInputValue}
          tagInputValue={tagInputValue}
        />
      )}
      {field.widget === "radio" && (
        <RedoRadioGroup
          disabled={disabled}
          options={field.options}
          setValue={(value) => handleFieldChange(field.path, value)}
          value={fieldValue}
        />
      )}
      {field.widget === "button-tab-group" && (
        <RedoButtonTabGroup<LeafFieldPathValue<T, typeof field.path>>
          disabled={disabled}
          onChange={(v) => handleFieldChange(field.path, v)}
          options={field.options.map((o) => ({
            el: o.element,
            value: o.value,
          }))}
          size="sm"
          value={fieldValue ?? field.defaultValue}
        />
      )}
      {field.widget === "inspector" &&
        (field.inline ? (
          <Inspector
            config={field.inspector}
            disabled={disabled}
            object={"nestedPath" in field ? object[field.nestedPath] : object}
            setObject={
              "nestedPath" in field
                ? (o) => setObject({ ...object, [field.nestedPath]: o })
                : setObject
            }
          />
        ) : (
          <Accordion disabled={disabled} preview={field.Preview?.(object)}>
            <Inspector
              config={field.inspector}
              disabled={disabled}
              object={"nestedPath" in field ? object[field.nestedPath] : object}
              setObject={
                "nestedPath" in field
                  ? (o) => setObject({ ...object, [field.nestedPath]: o })
                  : setObject
              }
            />
          </Accordion>
        ))}
      {field.widget === "custom" && (
        <field.Element value={object} valueChange={setObject} />
      )}
    </>
  );
}

function selectItemToDropdownItem<
  T extends {
    name: any;
    displayName?: ReactNode;
    DisplayIcon?: JSXElementConstructor<any>;
  },
>(selectItem: T): RedoListItem<T> {
  if (selectItem.displayName && typeof selectItem.displayName !== "string") {
    return {
      id: selectItem.name,
      type: "custom",
      value: selectItem,
      render: selectItem.displayName as ReactNode,
      useWrapper: true,
    };
  }

  const display =
    selectItem.displayName ||
    capitalized(allDelimitersToSpaces(selectItem.name ?? "")) ||
    "";

  const leadingItem: LeadingItem | undefined = selectItem.DisplayIcon
    ? { type: "icon", Icon: selectItem.DisplayIcon }
    : undefined;

  return {
    id: selectItem.name,
    type: "text",
    text: display.toString(),
    value: selectItem,
    leadingItem,
  };
}

const FieldSelectWidget = genericMemo(function FieldSelectWidget<
  T,
  P extends LeafFieldPath<T>,
>({
  field,
  fieldValue,
  disabled,
  handleFieldChange,
}: {
  field: SelectFieldConfig<T, P>;
  fieldValue: any;
  disabled?: boolean;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
}) {
  const selectedItem = field.options.find(
    (o) => (o.name ?? null) === (fieldValue ?? null),
  );

  return (
    <RedoSingleSelectDropdownInput
      disabled={disabled}
      options={field.options.map(selectItemToDropdownItem)}
      optionSelected={({ item }) =>
        handleFieldChange(
          field.path,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          typeof item === "string" ? item : (item.value as any).name,
        )
      }
      selectedItem={
        selectedItem ? selectItemToDropdownItem(selectedItem) : undefined
      }
      size="xs"
    />
  );
});

function EmailFieldWidget<T>({
  field,
  fieldValue,
  disabled,
  handleFieldChange,
}: {
  field: EmailFieldConfig<T>;
  fieldValue: any;
  disabled?: boolean;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
}) {
  const [touched, setTouched] = useState(false);

  const textChangeHandler = useHandler((value: string) => {
    handleFieldChange(field.path, value);
  });

  const handleBlur = useHandler(() => {
    setTouched(true);
    const currentValue = fieldValue || "";
    const trimmedValue = currentValue.trim();
    if (currentValue !== trimmedValue) {
      handleFieldChange(field.path, trimmedValue);
    }
  });

  const getErrorMessage = () => {
    if (field.error) {
      return field.error(fieldValue);
    }

    if (touched && fieldValue && !isValidEmail(fieldValue)) {
      return "Please enter a valid email address";
    }

    return undefined;
  };

  const errorMessage = getErrorMessage();

  return (
    <RedoTextInput
      description={errorMessage}
      maxLength={field.maxLength}
      onBlur={handleBlur}
      placeholder={field.placeholder ?? "example@domain.com"}
      setValue={textChangeHandler}
      state={errorMessage ? "error" : disabled ? "disabled" : "default"}
      value={fieldValue || ""}
    />
  );
}

function PhoneFieldWidget<T>({
  field,
  fieldValue,
  disabled,
  handleFieldChange,
}: {
  field: PhoneFieldConfig<T>;
  fieldValue: any;
  disabled?: boolean;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
}) {
  const [touched, setTouched] = useState(false);

  const textChangeHandler = useHandler((value: string) => {
    handleFieldChange(field.path, value);
  });

  const handleBlur = useHandler(() => {
    setTouched(true);
  });

  const getErrorMessage = () => {
    if (field.error) {
      return field.error(fieldValue);
    }

    if (touched && fieldValue && !isMobilePhone(fieldValue)) {
      return "Please enter a valid phone number";
    }

    return undefined;
  };

  const errorMessage = getErrorMessage();

  return (
    <RedoPhoneNumberInput
      errorText={errorMessage}
      onBlur={handleBlur}
      setValue={textChangeHandler}
      state={errorMessage ? "error" : disabled ? "disabled" : "default"}
      value={fieldValue || ""}
    />
  );
}

function UrlFieldWidget<T>({
  field,
  fieldValue,
  disabled,
  handleFieldChange,
}: {
  field: UrlFieldConfig<T>;
  fieldValue: any;
  disabled?: boolean;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
}) {
  const [touched, setTouched] = useState(false);

  const textChangeHandler = useHandler((value: string) => {
    handleFieldChange(field.path, value);
  });

  const handleBlur = useHandler(() => {
    setTouched(true);
    const currentValue = fieldValue || "";
    const trimmedValue = currentValue.trim();
    if (currentValue !== trimmedValue) {
      handleFieldChange(field.path, trimmedValue);
    }
  });

  const getErrorMessage = () => {
    if (field.error) {
      return field.error(fieldValue);
    }

    if (touched && fieldValue && !urlRegex.test(fieldValue)) {
      return "Please enter a valid URL (e.g., https://example.com)";
    }

    return undefined;
  };

  const errorMessage = getErrorMessage();

  return (
    <RedoTextInput
      description={errorMessage}
      maxLength={field.maxLength}
      onBlur={handleBlur}
      placeholder={field.placeholder ?? "https://example.com"}
      setValue={textChangeHandler}
      state={errorMessage ? "error" : disabled ? "disabled" : "default"}
      value={fieldValue || ""}
    />
  );
}

export const ImageUploadWidget = genericMemo(function ImageUploadWidget<T>({
  field,
  fieldValue,
  disabled,
  handleFieldChange,
  object,
  setObject,
}: {
  field: ImageUploadFieldConfig<T>;
  fieldValue: any;
  disabled?: boolean;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
  object: T;
  setObject: (object: T) => void;
}) {
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);

  const handleImageUrlChange = useHandler(async (newImageUrl: string) => {
    handleFieldChange(field.path, newImageUrl);
    field.onImageUrlChanged?.(newImageUrl);
  });

  return (
    <>
      <div
        className={inspectorStyles.imageUploadWrapper}
        data-web="ImageUploadWidget"
      >
        <ImageUpload
          accept={field.accept}
          autoOptimizeImage={field.autoOptimizeImage}
          caption={field.caption}
          className={field.className}
          disabled={disabled}
          maxFileSizeKb={field.maxFileSizeKb}
          maxImageWidthPx={field.maxImageWidthPx}
          onChange={(i) => handleImageUrlChange(i?.href ?? "")}
          preserveGifs={field.preserveGifs}
          recommendedLongestSidePx={field.recommendedLongestSidePx}
          showErrorMessages
          value={fieldValue}
        />
        {field.required && !fieldValue && (
          <Text fontSize="xs" textColor="error">
            Image is required
          </Text>
        )}
      </div>

      <Flex alignSelf="flex-start" dir="row" gap="sm" mt="sm">
        {field.isCroppingEnabled &&
          fieldValue &&
          !fieldValue.toLowerCase().endsWith(".gif") && (
            <>
              <RedoButton
                hierarchy="secondary"
                onClick={() => setIsImageEditorOpen(true)}
                size="sm"
                text="Crop"
              />
              <ImageEditorModal
                imageUrl={fieldValue}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                initialCrop={(object as Section.Image).cropConfigV2}
                isOpen={isImageEditorOpen}
                onCropComplete={(cropConfigV2) => {
                  setObject({ ...object, cropConfigV2 });
                }}
                setIsOpen={setIsImageEditorOpen}
              />
            </>
          )}
        {fieldValue && (
          <>
            <RedoButton
              hierarchy="secondary"
              onClick={() => {
                const imageUploadEl =
                  document.getElementById("image-upload-image");
                if (imageUploadEl) {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  if ((object as Section.Image).cropConfig) {
                    setObject({ ...object, cropConfig: undefined });
                  }

                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  (imageUploadEl as HTMLInputElement).click();
                }
              }}
              size="sm"
              text="Replace"
            />
            <RedoButton
              aria-label="Delete"
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => {
                handleFieldChange(field.path, "");
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                if ((object as Section.Image).cropConfig) {
                  setObject({ ...object, cropConfig: undefined });
                }
              }}
              size="sm"
              theme="destructive"
            />
          </>
        )}
      </Flex>
    </>
  );
});

export const TagFieldWidget = genericMemo(function TagFieldWidget<T>({
  field,
  fieldValue,
  disabled,
  handleFieldChange,
  tagInputValue,
  setTagInputValue,
}: {
  field: TagFieldConfig<T>;
  fieldValue: any;
  disabled?: boolean;
  handleFieldChange: (path: LeafFieldPath<T>, value: any) => void;
  tagInputValue: string;
  setTagInputValue: (value: string) => void;
}) {
  const [showUnsubmittedError, setShowUnsubmittedError] = useState(false);
  const handleBlur = useHandler(() => {
    if (tagInputValue.trim().length > 0) {
      setShowUnsubmittedError(true);
    }
  });
  const handleInputChange = useHandler((value: string) => {
    setTagInputValue(value);
    if (value.trim().length === 0) {
      setShowUnsubmittedError(false);
    }
  });
  const handleTagsChange = useHandler((tags: string[]) => {
    handleFieldChange(field.path, tags);
    setShowUnsubmittedError(false);
  });
  const getErrorMessage = () => {
    if (field.error) {
      return field.error(fieldValue);
    }
    if (showUnsubmittedError && tagInputValue.trim().length > 0) {
      return formatDelimitersMessage(field.delimiters || ["newline"]);
    }
    return undefined;
  };
  const errorMessage = getErrorMessage();
  return (
    <RedoTagInputField
      delimiters={field.delimiters}
      description={errorMessage}
      inputValue={tagInputValue}
      onBlur={handleBlur}
      placeholder={field.placeholderText}
      setInputValue={handleInputChange}
      setTags={handleTagsChange}
      state={errorMessage ? "error" : disabled ? "disabled" : "default"}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      tags={((fieldValue as string[]) || []).map((t) => ({ text: t }))}
    />
  );
});
